import { I18Text } from "src/types/i18Text";
import { Body as SundayBody, Title as SundayTitle } from "sunday-component";
import {
  CoverageItemType,
  PublicDisplayImageType,
} from "src/types/productCategory";
import styled from "styled-components";
import { color } from "src/styles/globals";
import { useRouter } from "next/router";
import { numberToCurrency } from "src/utils/currency";
import { useTranslation } from "next-i18next";
import { down } from "styled-breakpoints";
import { Row, Col, Skeleton as AntdSkeleton, Divider } from "antd";
import { useState } from "react";
import { LabelType } from "src/types/label";
import { COVERAGE_DETAIL_TEMPLATE_OPTION } from "src/constants/template";
import CommonButton from "../Common/Button";
import { CoverageTableSection } from "./CoverageTableSection";
import { SpecialBenefitSection } from "./SpecialBenefitSection";
import Label from "../Common/Label";

const Container = styled.div`
  background: white;
  padding: 16px;
  margin: 0px auto 40px auto;

  border: 1px solid ${color.charcoal03};
  border-radius: 16px;
  max-width: 938px;

  ${down("md")} {
    border: 0px;
    padding: 0px 16px 16px 16px;
  }
`;

const HeaderContainer = styled.div``;

const InsurerContainer = styled.div`
  margin-bottom: 2px;
`;

const LogoImg = styled.img`
  margin: auto;
  display: block;
  height: 40px;
  width: 40px;
`;

const InsurerTitleText = styled(SundayBody)`
  color: ${color.charcoal09};
  text-align: center;
`;

const PlanTitleText = styled(SundayTitle)`
  color: ${color.charcoal10};
  text-align: center;
`;

const PriceText = styled(SundayTitle)`
  color: var(--ui-form-plans_card-price-text-color);
  text-align: center;
  margin: 0px 0px 0px 2px;
`;

const PriceInlineText = styled(SundayBody)`
  color: ${color.charcoal09};
  display: inline-block;
`;

const PlanDescriptionTextA = styled(SundayBody)`
  color: ${color.charcoal10};
  text-align: center;
  margin: 24px 0px;
`;

const PlanDescriptionTextB = styled(SundayBody)`
  color: ${color.charcoal10};
  text-align: left;
  margin: 24px 0px;
  margin-left: auto;
  margin-right: auto;
  width: fit-content;
  line-height: 1.25;
`;

const RemarkText = styled(SundayTitle)`
  color: ${color.warningYellow03};
  text-align: center;
`;

const PriceSkeletonSection = styled.div`
  display: inline-block;
`;

const PriceSkeleton = styled(AntdSkeleton.Button)`
  margin: 0px !important;
  width: 100px !important;
`;

const ButtonText = styled(SundayTitle)`
  font-size: 21px;
`;

const Button = styled(CommonButton)``;

const ButtonSectionContainer = styled.div<{
  maxWidth: string;
}>`
  max-width: ${(props) => props.maxWidth};
  margin: auto;
  ${down("md")} {
    max-width: 100%;
  }
`;

export type RemarkTextSectionProps = {
  isMatchedPricingItem: boolean;
  remarkPriceEspeciallyForYou: string;
  remarkPriceDependingOnInsured: string;
};

const RemarkTextSection = ({
  isMatchedPricingItem,
  remarkPriceEspeciallyForYou,
  remarkPriceDependingOnInsured,
}: RemarkTextSectionProps) => (
  <RemarkText level={10}>
    {isMatchedPricingItem
      ? remarkPriceEspeciallyForYou
      : remarkPriceDependingOnInsured}
  </RemarkText>
);

export type PriceTextSectionProps = {
  isMatchedPricingItem: boolean;
  isPriceLoading: boolean;
  price: number;
  priceStarter: string;
  coveragePeriod: string;
};

const PriceTextSection = ({
  isMatchedPricingItem,
  isPriceLoading,
  price,
  priceStarter,
  coveragePeriod,
}: PriceTextSectionProps) => (
  <>
    <PriceText level={4}>
      {!isMatchedPricingItem && (
        <>
          <PriceInlineText level={9}>{priceStarter}</PriceInlineText>{" "}
        </>
      )}
      {isPriceLoading ? (
        <PriceSkeletonSection>
          <PriceSkeleton active size="small" />
        </PriceSkeletonSection>
      ) : (
        `${numberToCurrency(+price)}*`
      )}{" "}
      <PriceInlineText level={9}>{coveragePeriod}</PriceInlineText>
    </PriceText>
  </>
);

export type HeaderButtonSectionProps = {
  isMatchedPricingItem: boolean;
  onOpenEntryForm: Function;
  reCalculatePriceButtonText: string;
  handleGoToAppForm: Function;
  isGoToAppFormLoading: boolean;
  buyThisPlanButtonText: string;
  calculatePriceButtonText: string;
};

const HeaderButtonSection = ({
  isMatchedPricingItem,
  onOpenEntryForm,
  reCalculatePriceButtonText,
  handleGoToAppForm,
  isGoToAppFormLoading,
  buyThisPlanButtonText,
  calculatePriceButtonText,
}: HeaderButtonSectionProps) => (
  <ButtonSectionContainer maxWidth={isMatchedPricingItem ? "646px" : "330px"}>
    <Row justify="center" gutter={[24, 16]}>
      {isMatchedPricingItem ? (
        <>
          <Col
            lg={{ span: 12, order: 1 }}
            sm={{ span: 24, order: 2 }}
            xs={{ span: 24, order: 2 }}
          >
            <Button
              data-testid="re-calculate-price-button"
              data-gtm-input-code="re-calculate-price-button"
              type="outlined"
              onClick={onOpenEntryForm}
              fullWidth
            >
              <ButtonText level={7}>{reCalculatePriceButtonText}</ButtonText>
            </Button>
          </Col>
          <Col
            lg={{ span: 12, order: 2 }}
            sm={{ span: 24, order: 1 }}
            xs={{ span: 24, order: 1 }}
          >
            <Button
              data-testid="buy-this-plan-button"
              data-gtm-input-code="buy-this-plan-button"
              onClick={handleGoToAppForm}
              fullWidth
              loading={isGoToAppFormLoading}
            >
              <ButtonText level={7}>{buyThisPlanButtonText}</ButtonText>
            </Button>
          </Col>
        </>
      ) : (
        <Col span={24}>
          <Button
            data-testid="calculate-price-button"
            data-gtm-input-code="calculate-price-button"
            fullWidth
            onClick={onOpenEntryForm}
          >
            <ButtonText level={7}>{calculatePriceButtonText}</ButtonText>
          </Button>
        </Col>
      )}
    </Row>
  </ButtonSectionContainer>
);

export type PlanContentDirectDetailProps = {
  insurer: {
    logo: PublicDisplayImageType;
    displayName: I18Text;
  };
  product: {
    displayName: I18Text;
  };
  plan: {
    displayName: I18Text;
    coveragePeriod: string;
    policyDetailUrl: I18Text;
    description: I18Text;
  };
  labels: LabelType[];
  allCoverageItems: {
    coverageItem: CoverageItemType;
    value: string | I18Text | boolean;
  }[];
  specialBenefitItems: {
    code: string;
    icon: PublicDisplayImageType;
    displayName: I18Text;
  }[];
  price: number;
  insurerPlanCode: string;
  isPriceLoading: boolean;
  isMatchedPricingItem: boolean;
  onOpenEntryForm: Function;
  onGoToAppForm: Function;
  CoverageTablePrimaryColor: string;
  CoverageTableSecondaryColor: string;
  template?: string;
};

const PlanContentDirectLinkDetail = ({
  insurer,
  product,
  plan,
  price,
  insurerPlanCode,
  labels = [],
  allCoverageItems,
  specialBenefitItems,
  isPriceLoading,
  isMatchedPricingItem,
  onOpenEntryForm = () => {},
  onGoToAppForm = () => {},
  CoverageTablePrimaryColor,
  CoverageTableSecondaryColor,
  template = COVERAGE_DETAIL_TEMPLATE_OPTION.A,
}: PlanContentDirectDetailProps) => {
  const { locale } = useRouter();
  const { t } = useTranslation(["plan-page", "plan-content-direct-link"]);

  const [isGoToAppFormLoading, setIsGoToAppFormLoading] =
    useState<boolean>(false);

  const handleGoToAppForm = async () => {
    setIsGoToAppFormLoading(true);
    await onGoToAppForm();
    setIsGoToAppFormLoading(false);
  };

  return (
    <Container>
      <HeaderContainer>
        {labels.length > 0 && (
          <Row justify="center">
            {labels.map((label) => (
              <Label
                key={label.displayName.en}
                label={label.displayName[locale]}
                textColor={label.colorCode.text}
                backgroundColor={label.colorCode.background}
              />
            ))}
          </Row>
        )}
        <InsurerContainer>
          <LogoImg key={insurer.logo.code} src={insurer.logo.url} />
          <InsurerTitleText level={9}>
            {insurer.displayName[locale]}
          </InsurerTitleText>
        </InsurerContainer>
        <PlanTitleText level={7}>{product.displayName[locale]}</PlanTitleText>
        <PlanTitleText level={7}>{plan.displayName[locale]}</PlanTitleText>
        <PriceTextSection
          isMatchedPricingItem={isMatchedPricingItem}
          isPriceLoading={isPriceLoading}
          price={price}
          priceStarter={t("planDetail.priceStarter", {
            ns: "plan-content-direct-link",
          })}
          coveragePeriod={
            plan.coveragePeriod
              ? t(`coverageDetail.coveragePeriod.${plan.coveragePeriod}`, {
                  ns: "plan-page",
                })
              : ""
          }
        />
        <RemarkTextSection
          isMatchedPricingItem={isMatchedPricingItem}
          remarkPriceEspeciallyForYou={t(
            "planDetail.remarkPriceEspeciallyForYou",
            {
              ns: "plan-content-direct-link",
            }
          )}
          remarkPriceDependingOnInsured={t(
            "planDetail.remarkPriceDependingOnInsured",
            {
              ns: "plan-content-direct-link",
            }
          )}
        />
        {template === COVERAGE_DETAIL_TEMPLATE_OPTION.A && (
          <PlanDescriptionTextA level={7}>
            {plan.description[locale]}
          </PlanDescriptionTextA>
        )}
        {template === COVERAGE_DETAIL_TEMPLATE_OPTION.B && (
          <PlanDescriptionTextB level={7}>
            {plan.description[locale]}
          </PlanDescriptionTextB>
        )}
        <HeaderButtonSection
          isMatchedPricingItem={isMatchedPricingItem}
          onOpenEntryForm={onOpenEntryForm}
          reCalculatePriceButtonText={t("planDetail.reCalculatePriceButton", {
            ns: "plan-content-direct-link",
          })}
          handleGoToAppForm={handleGoToAppForm}
          isGoToAppFormLoading={isGoToAppFormLoading}
          buyThisPlanButtonText={t("planDetail.buyThisPlanButton", {
            ns: "plan-content-direct-link",
          })}
          calculatePriceButtonText={t("planDetail.calculatePriceButton", {
            ns: "plan-content-direct-link",
          })}
        />
      </HeaderContainer>
      <Divider />
      <CoverageTableSection
        allCoverageItems={allCoverageItems}
        insurerPlanCode={insurerPlanCode}
        policyDetailUrl={plan.policyDetailUrl}
        tablePrimaryColor={CoverageTablePrimaryColor}
        tableSecondaryColor={CoverageTableSecondaryColor}
      />
      <Divider />
      <SpecialBenefitSection
        specialBenefitItems={specialBenefitItems}
        insurerPlanCode={insurerPlanCode}
      />
    </Container>
  );
};

export default PlanContentDirectLinkDetail;
